import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, effect, inject, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalRegisterMemberDetailComponent } from '../../../modals/cityparking/register-member/modal-register-member-detail/modal-register-member-detail.component';
import {
  RegisterMemberDashboardStat,
  RegisterMemberTableDto,
  RegisterMemberTableRow,
} from '../../../../../core/models/cityparking/register-member-list.model';
import { MapperRegisterMemberListService } from '../../../../../core/mappers/cityparking/mapper-register-member-list.service';
import { ApiRegisterMemberListService } from '../../../../../core/services/api/cityparking/api-register-member-list.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';

@Component({
  selector: 'app-register-member-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalRegisterMemberDetailComponent],
  templateUrl: './register-member-list.component.html',
  styleUrl: './register-member-list.component.css',
  providers: [DatePipe],
})
export class RegisterMemberListComponent implements OnInit {
  private static readonly DEFAULT_DATE_RANGE_DAYS = 30;

  readonly siteState = inject(SiteStateService);
  private readonly apiService = inject(ApiRegisterMemberListService);
  private readonly mapper = inject(MapperRegisterMemberListService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly datePipe = inject(DatePipe);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  protected Math = Math;
  rows: RegisterMemberTableRow[] = [];
  dashboardStats: RegisterMemberDashboardStat[] = [];

  searchText = '';
  searchType: 'memberName' | 'license' | 'phone' | 'requestNo' = 'memberName';
  isSearchTypeDropdownOpen = false;
  filterStatus = 0;

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems = 0;
  totalPages = 1;

  startDate: string | null = null;
  endDate: string | null = null;

  isLoading = false;
  isDashboardLoading = false;
  showDashboard = true;

  isModalDetailOpen = false;
  selectedRegisterMemberId = 0;
  userId = 0;

  constructor() {
    this.applyDefaultDateRange();

    const userIdLocal = localStorage.getItem('userId');
    this.userId = userIdLocal
      ? Number(userIdLocal)
      : Number(this.cookieService.get('user_id'));

    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;

      untracked(() => {
        this.currentPage = 1;
        this.loadTable();
        this.loadDashboard();
      });
    });
  }

  ngOnInit(): void {
    if (!this.userId) {
      const userIdLocal = localStorage.getItem('userId');
      this.userId = userIdLocal
        ? Number(userIdLocal)
        : Number(this.cookieService.get('user_id'));
    }
    if (this.selectedSiteId && this.userId) {
      this.loadTable();
      this.loadDashboard();
    }
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get menuId(): number {
    return this.permissionService.getMenuId(this.router.url) ?? 0;
  }

  get tableDateRangeLabel(): string {
    if (this.startDate && this.endDate) {
      return `${this.formatDisplayDate(this.startDate)} - ${this.formatDisplayDate(this.endDate)}`;
    }
    return 'ทั้งหมด';
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'license':
        return 'ทะเบียนรถ';
      case 'phone':
        return 'เบอร์โทร';
      case 'requestNo':
        return 'เลขที่คำขอ';
      default:
        return 'ชื่อสมาชิก';
    }
  }

  get searchPlaceholder(): string {
    switch (this.searchType) {
      case 'license':
        return 'ค้นหาทะเบียนรถ...';
      case 'phone':
        return 'ค้นหาเบอร์โทร...';
      case 'requestNo':
        return 'ค้นหาเลขที่คำขอ...';
      default:
        return 'ค้นหาชื่อสมาชิก...';
    }
  }

  statusLabel(displayStatus: number): string {
    return this.mapper.getDisplayStatusLabel(displayStatus);
  }

  statusClass(displayStatus: number): string {
    return this.mapper.getDisplayStatusClass(displayStatus);
  }

  statusDotClass(displayStatus: number): string {
    return this.mapper.getDisplayStatusDotClass(displayStatus);
  }

  formatDisplayDate(value: string): string {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }

  formatDateTime(value: string | null): string {
    if (!value) return '-';
    return this.datePipe.transform(value, 'dd/MM/yyyy HH:mm') || '-';
  }

  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  onRefresh(): void {
    this.searchText = '';
    this.searchType = 'memberName';
    this.filterStatus = 0;
    this.applyDefaultDateRange();
    this.currentPage = 1;
    this.loadTable();
    this.loadDashboard();
  }

  onDateChange(): void {
    if ((this.startDate && !this.endDate) || (!this.startDate && this.endDate)) {
      return;
    }
    this.currentPage = 1;
    this.loadTable();
    this.loadDashboard();
  }

  clearDateFilter(): void {
    this.applyDefaultDateRange();
    this.currentPage = 1;
    this.loadTable();
    this.loadDashboard();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  selectStatus(status: number): void {
    this.filterStatus = this.filterStatus === status ? 0 : status;
    this.currentPage = 1;
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize = Number(element.value);
    this.currentPage = 1;
    this.loadTable();
  }

  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'memberName' | 'license' | 'phone' | 'requestNo'): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  openDetailModal(id: number): void {
    this.selectedRegisterMemberId = id;
    this.isModalDetailOpen = true;
  }

  closeDetailModal(): void {
    this.isModalDetailOpen = false;
    this.selectedRegisterMemberId = 0;
  }

  onDetailSaved(): void {
    this.closeDetailModal();
    this.loadTable();
    this.loadDashboard();
  }

  private applyDefaultDateRange(): void {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - RegisterMemberListComponent.DEFAULT_DATE_RANGE_DAYS);
    this.endDate = this.formatDate(end);
    this.startDate = this.formatDate(start);
  }

  private formatDate(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  private loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.rows = [];
      this.totalItems = 0;
      this.totalPages = 1;
      return;
    }

    this.isLoading = true;
    const status = this.mapper.mapStatusFilter(this.filterStatus);

    this.apiService
      .getRegisterMemberForTable({
        siteId,
        userId: this.userId,
        page: this.currentPage,
        pageSize: this.pageSize,
        searchField: this.searchText.trim() ? this.mapper.mapSearchField(this.searchType) : undefined,
        searchText: this.searchText.trim() || undefined,
        status: status ?? 'all',
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined,
      })
      .subscribe({
        next: (res : RegisterMemberTableDto) => {
          const mapped = this.mapper.mapTableFromDto(res);
          this.rows = mapped.items;
          this.totalItems = mapped.total;
          this.totalPages = mapped.totalPages || Math.ceil(mapped.total / (this.pageSize || 1));
          this.isLoading = false;
        },
        error: (err) => {
          console.error('[API Error] getRegisterMemberForTable:', err);
          this.rows = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this.isLoading = false;
          this.toast.error('ไม่สามารถโหลดรายการสมัครสมาชิกได้');
        },
      });
  }

  private loadDashboard(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) {
      this.dashboardStats = [];
      return;
    }

    this.isDashboardLoading = true;
    this.apiService
      .getRegisterMemberMarketingDashboard(
        siteId,
        this.userId,
        this.startDate || undefined,
        this.endDate || undefined
      )
      .subscribe({
        next: (res) => {
          this.dashboardStats = this.mapper.mapDashboardStats(res);
          this.isDashboardLoading = false;
        },
        error: (err) => {
          console.error('[API Error] getRegisterMemberMarketingDashboard:', err);
          this.dashboardStats = [];
          this.isDashboardLoading = false;
        },
      });
  }
}
